/** @jsx jsx */
import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Box, jsx } from 'theme-ui'
import AboutCansIconListSection from './AboutCansIconList'
import AboutIconListSection from './AboutIconList'
import ArticleListSection from './ArticleList'
import AttentivePhoneNumberCaptureSection from './AttentivePhoneNumberCapture'
import AwardCarouselSection from './AwardCarousel'
import AwardListByPackSection from './AwardListByPack'
import BasicTextPageSection from './BasicTextPage'
import BasicTextSection from './BasicText'
import BodyCopySection from './BodyCopy'
import CallToAction from './CallToAction'
import CanClubHeroSection from './CanClubHero'
import ChooseMembershipSection from './ChooseMembership'
import CtaSection from './CtaSection'
import EventListSection from './EventList'
import FaqPageSection from './FaqPage'
import FaqSection from './Faq'
import FeaturedArticlesSection from './FeaturedArticles'
import FeaturedGiftsSection from './FeaturedGifts'
import FeaturedWinesSection from './FeaturedWines'
import FormSection from './Form'
import FullBleedImageCalloutSection from './FullBleedImageCallout'
import FullBleedImageCalloutLargeSection from './FullBleedImageCalloutLarge'
import GroupGiftingEntrySection from './GroupGiftingEntry'
import GroupGiftingHeroSection from './GroupGiftingHero'
import HeroSection from './Hero'
import IconListSection from './IconList'
import IconListExpandedSection from './IconListExpanded'
import InstagramSection from './Instagram'
import KlaviyoEmailCaptureSection from './KlaviyoEmailCapture'
import LifestyleSection from './Lifestyle'
import LogoPoolSection from './LogoPool'
import MakerMegaCarouselSection from './MakerMegaCarousel'
import NumberedStepsListSection from './NumberedStepsList'
import PageHeadingAboutMakerSection from './PageHeadingAboutMaker'
import PageHeadingBasicSection from './PageHeadingBasic'
import PageHeadingImageSection from './PageHeadingImage'
import PressCarouselSection from './PressCarousel'
import PressListSection from './PressList'
import ProductListSection from './ProductList'
import ProductListLargeSection from './ProductListLarge'
import QuoteSection from './Quote'
import ReviewListSection from './ReviewList'
import ReviewsSection from './Reviews'
import SideBySideSection from './SideBySide'
import SimpleContentCardListSection from './SimpleContentCardList'
import SpotlightSection from './Spotlight'
import StoreLocatorSection from './StoreLocator'
import SweepstakesEmailCaptureSection from './SweepstakesEmailCapture'
import TeamListSection from './TeamList'
import TradeLandingHeroSection from './TradeLandingHero'
import TradeMaterialsListSection from './TradeMaterialsList'
import VirtualEventsHeroSection from './VirtualEventsHero'

const layoutType = (section) => {
  return section.layout && section.layout.split('|')[0].trim()
}

const ContentSection = ({ section, isTrade }) => {
  const layout = layoutType(section)

  if (layout) {
    return (
      <Box>
        {layout === 'About Cans Icon List' && (
          <AboutCansIconListSection
            title={section.title}
            description={section.description}
            media={section.media}
            cta={
              section.cta && <CallToAction content={section.cta} type="text" />
            }
          />
        )}

        {layout === 'About Maker Icon List' && (
          <AboutIconListSection
            title={section.title}
            cards={section.media}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'Article List' && (
          <ArticleListSection
            title={section.title}
            categories={section.media}
          />
        )}

        {layout === 'Attentive Phone Number Capture' && (
          <AttentivePhoneNumberCaptureSection
            title={section.title}
            description={section.description}
            images={section.images}
          />
        )}

        {layout === 'Award Carousel' && (
          <AwardCarouselSection
            title={section.title}
            description={section.description}
            packs={section.media}
            cta={section.cta && <CallToAction content={section.cta} />}
            isTrade={isTrade}
          />
        )}

        {layout === 'Award List by Pack' && (
          <AwardListByPackSection
            title={section.title}
            packs={section.media}
            isTrade={isTrade}
          />
        )}

        {layout === 'Basic Text Page' && (
          <BasicTextPageSection
            title={section.title}
            description={section.description}
          />
        )}

        {layout === 'Basic Text Section' && (
          <BasicTextSection
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
            title={section.title}
            cta={section.cta && <CallToAction content={section.cta} />}
            description={section.description}
          />
        )}

        {layout === 'Body Copy' && <BodyCopySection body={section.body} />}

        {layout === 'Can Club Hero' && (
          <CanClubHeroSection
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            images={section.images}
          />
        )}

        {layout === 'Can Club Options' && (
          <ChooseMembershipSection
            title={section.title}
            media={section.media}
            footer={section.footer}
          />
        )}

        {layout === 'CTA - Can Club' && (
          <CtaSection
            variant="can-club"
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'CTA - Delivery' && (
          <CtaSection
            variant="delivery"
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'CTA - Press & Awards' && (
          <CtaSection
            variant="press-awards"
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'Event List' && (
          <EventListSection
            title={section.title}
            description={section.description}
            events={section.media}
          />
        )}

        {layout === 'FAQ Page' && (
          <FaqPageSection
            topics={section.media}
            title={section.title}
            description={section.description}
          />
        )}

        {layout === 'FAQ Section' && (
          <FaqSection
            topic={section.media && section.media[0]}
            title={section.title}
            description={section.description}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Featured Articles' && (
          <FeaturedArticlesSection
            articles={section.media}
            title={section.title}
            description={section.description}
          />
        )}

        {layout === 'Featured Gifts' && (
          <FeaturedGiftsSection title={section.title} gifts={section.media} />
        )}

        {layout === 'Featured Product List' && (
          <ProductListSection
            listings={section.media}
            title={section.title}
            cta={
              section.cta && <CallToAction content={section.cta} type="text" />
            }
          />
        )}

        {layout === 'Featured Product List (Large)' && (
          <ProductListLargeSection
            products={section.media}
            title={section.title}
            cta={section.cta && <CallToAction content={section.cta} />}
            collapseMarginTop={section.collapseMarginTop}
          />
        )}

        {layout === 'Featured Product List (Specs)' && (
          <FeaturedWinesSection
            title={section.title}
            description={section.description}
            media={section.media}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'Featured Product List (Specs, Narrow)' && (
          <FeaturedWinesSection
            title={section.title}
            description={section.description}
            isNarrow={true}
            media={section.media}
            cta={section.cta && <CallToAction content={section.cta} />}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
            collapseMarginTop={section.collapseMarginTop}
          />
        )}

        {layout === 'Form' && (
          <FormSection
            title={section.title}
            description={section.description}
            form={section.media && section.media[0] && section.media[0].form}
            footer={section.footer}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Full Bleed Image Callout' && (
          <FullBleedImageCalloutSection
            image={section.images.length && section.images[0]}
            title={section.title}
            description={section.description}
            cta={
              section.cta && <CallToAction content={section.cta} type="text" />
            }
          />
        )}

        {layout === 'Full Bleed Image Callout (Large)' && (
          <FullBleedImageCalloutLargeSection
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
            image={section.images.length && section.images[0]}
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            footer={section.footer}
          />
        )}

        {layout === 'Group Gifting Entry' && (
          <GroupGiftingEntrySection
            images={section.images}
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            footer={section.footer}
          />
        )}

        {layout === 'Group Gifting Hero' && (
          <GroupGiftingHeroSection
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            images={section.images}
          />
        )}

        {layout === 'Homepage Hero' && (
          <HeroSection
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            images={section.images}
          />
        )}

        {layout === 'Homepage Lifestyle List' && (
          <LifestyleSection
            title={section.title}
            description={section.description}
            cards={section.media}
          />
        )}

        {layout === 'Icon List' && (
          <IconListSection
            title={section.title}
            subtitle={section.subtitle}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Icon List (Expanded)' && (
          <IconListExpandedSection
            title={section.title}
            description={section.description}
            cards={section.media}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Instagram Post List' && (
          <InstagramSection
            title={section.title}
            subtitle={section.subtitle}
            posts={section.media}
          />
        )}

        {layout === 'Klaviyo Email Capture' && (
          <KlaviyoEmailCaptureSection
            title={section.title}
            description={section.description}
            formId={section.subtitle}
            footer={section.footer}
            images={section.images}
            success={section.cta}
          />
        )}

        {layout === 'Logo Pool' && (
          <LogoPoolSection
            title={section.title}
            subtitle={section.subtitle}
            description={section.description}
            images={section.images}
          />
        )}

        {layout === 'Logo Pool (Condensed)' && (
          <LogoPoolSection
            title={section.title}
            subtitle={section.subtitle}
            description={section.description}
            images={section.images}
            condensed={true}
          />
        )}

        {layout === 'Maker Mega Carousel' && (
          <MakerMegaCarouselSection
            title={section.title}
            subtitle={section.subtitle}
            description={section.description}
            makers={section.media}
          />
        )}

        {layout === 'Numbered Steps List' && (
          <NumberedStepsListSection
            title={section.title}
            subtitle={section.subtitle}
            cards={section.media}
          />
        )}

        {layout === 'Page Heading - About Maker' && (
          <PageHeadingAboutMakerSection
            title={section.title}
            subtitle={section.subtitle}
            description={section.description}
            cta={
              section.cta && <CallToAction content={section.cta} type="text" />
            }
            images={section.images}
          />
        )}

        {layout === 'Page Heading - Basic' && (
          <PageHeadingBasicSection
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            footer={section.footer}
          />
        )}

        {layout === 'Page Heading - Image' && (
          <PageHeadingImageSection
            title={section.title}
            subtitle={section.subtitle}
            image={section.images.length && section.images[0]}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            footer={section.footer}
          />
        )}

        {layout === 'Press Carousel' && (
          <PressCarouselSection
            title={section.title}
            press={section.media}
            variant="short"
          />
        )}

        {layout === 'Press Carousel (Long)' && (
          <PressCarouselSection
            title={section.title}
            press={section.media}
            variant="long"
          />
        )}

        {layout === 'Press List' && (
          <PressListSection
            title={section.title}
            press={section.media}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'Quote Card List' && (
          <QuoteSection
            quotes={section.media}
            title={section.title}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Review List' && (
          <ReviewListSection
            title={section.title}
            description={section.description}
            media={section.media}
            variant="page"
          />
        )}

        {layout === 'Review List (Landing)' && (
          <ReviewListSection
            title={section.title}
            subtitle={section.subtitle}
            media={section.media}
            variant="section"
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Side By Side (Form)' && (
          <SideBySideSection
            slug={section.slug}
            title={section.title}
            description={section.description}
            media={section.media}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
            variant="form"
          />
        )}

        {layout === 'Side By Side (Generic)' && (
          <SideBySideSection
            slug={section.slug}
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            footer={section.footer}
            media={section.media}
            image={section.images?.[0]}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
            variant="generic"
          />
        )}

        {layout === 'Side By Side (Quote)' && (
          <SideBySideSection
            description={section.description}
            footer={section.footer}
            image={section.images?.[0]}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
            variant="quote"
          />
        )}

        {layout === 'Site Reviews' && (
          <ReviewsSection
            title={section.title}
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Simple Content Card List' && (
          <SimpleContentCardListSection
            title={section.title}
            cards={section.media}
            description={section.description}
          />
        )}

        {layout === 'Spotlight' && (
          <SpotlightSection
            heading={section.title}
            image={section.media?.[0]?.media}
            title={section.media?.[0]?.title}
            subtitle={section.media?.[0]?.subtitle}
            description={section.media?.[0]?.description}
            cta={
              section.media?.[0]?.cta && (
                <CallToAction content={section.media[0].cta} />
              )
            }
            bgOverride={section.bgOverride && `background${section.bgOverride}`}
          />
        )}

        {layout === 'Store Locator' && (
          <StoreLocatorSection
            title={section.title}
            description={section.description}
            footer={section.footer}
            cta={section.cta && <CallToAction content={section.cta} />}
          />
        )}

        {layout === 'Sweepstakes Email Capture' && (
          <SweepstakesEmailCaptureSection
            title={section.title}
            description={section.description}
            body={section.body}
            sweepstakesId={section.subtitle.split('|')[0]}
            klaviyoListId={section.subtitle.split('|')[1]}
            footer={section.footer}
            images={section.images}
            success={section.cta}
          />
        )}

        {layout === 'Team List' && (
          <TeamListSection
            title={section.title}
            description={section.description}
            media={section.media}
            footer={section.footer}
            cta={
              section.cta && <CallToAction content={section.cta} type="text" />
            }
            isTrade={isTrade}
          />
        )}

        {layout === 'Trade Landing Hero' && (
          <TradeLandingHeroSection
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            images={section.images}
          />
        )}

        {layout === 'Trade Materials List' && (
          <TradeMaterialsListSection
            title={section.title}
            description={section.description}
            media={section.media}
          />
        )}

        {layout === 'Virtual Events Hero' && (
          <VirtualEventsHeroSection
            title={section.title}
            description={section.description}
            cta={section.cta && <CallToAction content={section.cta} />}
            images={section.images}
          />
        )}
      </Box>
    )
  }

  return <Fragment />
}

ContentSection.propTypes = {
  section: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.shape({
      description: PropTypes.string,
    }),
    media: PropTypes.arrayOf(PropTypes.shape({})),
  }),
}

export default ContentSection
